import {
  WSActions,
  WSCard,
  WSInfoBox,
  WSList
} from "@wingspanhq/fe-component-library";
import { MemberWithholdingStatus } from "@wingspanhq/users/dist/lib/interfaces";
import { useWSMutation } from "@ws-react-query";
import { WSErrorMessage } from "../../../../../components/WSErrorMessage/WSErrorMessage";
import { useUserId } from "../../../../../query/hooks/helpers";
import { QUERY_USERS_MEMBER_PROFILE } from "../../../../../query/users/keys";
import { useQueryMemberProfile } from "../../../../../query/users/queries";
import { usersService } from "../../../../../services/users";
import { Quote } from "../../../../../shared/components/Quote";
import { useModalHowItWorks } from "../../../../Taxes/modals/ModalHowItWorks";
import { useOnboardingContext } from "../../../Context";
import { TaxWithholdingsContext } from "../types";

type Props = {
  onBack?: () => void;
  onNext?: (value: "activate" | "skip") => void;
};

export const ReviewTaxWithholdingsRate: React.FC<Props> = props => {
  const [_, setContext] = useOnboardingContext<TaxWithholdingsContext>();
  const modalHowItWorks = useModalHowItWorks();
  const userId = useUserId();
  const queryMember = useQueryMemberProfile(userId);
  const taxWithholdingRate =
    queryMember.data?.profile.withholdings?.tax?.rate || 0;

  const [activate, activateMeta] = useWSMutation(
    async () => {
      await usersService.member.update(userId, {
        memberId: userId,
        profile: {
          withholdings: {
            tax: {
              status: MemberWithholdingStatus.Active
            }
          }
        }
      });
    },
    {
      awaitDependencies: [QUERY_USERS_MEMBER_PROFILE],
      onSuccess: () => {
        props.onNext?.("activate");
      }
    }
  );

  return (
    <WSList gap="2XL">
      <WSCard
        header={{
          label: "Your tax withholding settings",
          value: {
            action: {
              label: "Edit",
              onClick: props.onBack,
              type: "button"
            }
          }
        }}
      >
        <WSInfoBox>
          Withholding rate: {taxWithholdingRate}% of gross payments
        </WSInfoBox>
      </WSCard>

      <WSCard
        header={{
          label: "What happens next:",
          value: {
            action: {
              label: "How it works",
              onClick: () => {
                modalHowItWorks.open({
                  taxRate: taxWithholdingRate
                });
              },
              type: "button"
            }
          }
        }}
      >
        <WSList gap="L">
          <WSInfoBox transparent>
            <ul>
              <li>We'll create your dedicated tax savings account</li>
              <li>
                Future invoice payments will automatically split per your
                settings
              </li>
              <li>
                Your tax withholdings dashboard will show real-time balance and
                transactions
              </li>
              <li>
                We'll send reminders before each quarterly payment deadline
              </li>
            </ul>
          </WSInfoBox>

          <WSInfoBox title="Quarterly payment reminders">
            <p>
              We'll notify you before each quarterly deadline with:
              <ul>
                <li>Your current tax savings balance</li>
                <li>Estimated payment amount</li>
                <li>Instructions for paying via Wingspan</li>
                <li>Link to access your funds</li>
              </ul>
            </p>

            <p>
              <b>Flexibility & control</b>
              <ul>
                <li>Change your rate anytime in Settings</li>
                <li>Pause withholding with one click</li>
                <li>Your balance is always accessible</li>
              </ul>
            </p>
          </WSInfoBox>

          <Quote>
            <p>
              <i>
                “As a freelancer the included financial tools are a huge help.
                The tax related tools in particular are super useful and easy to
                understand. Wingspan can automatically set aside tax
                withholdings and mark items as business expenses to help you get
                an idea of what your tax burden will be. Game changer for my
                business."
              </i>
            </p>
            <p>- Beatrice Land (Wingspan Contractor)</p>
          </Quote>
        </WSList>
      </WSCard>

      <WSErrorMessage contextKey="TaxFilling" error={activateMeta.error} />

      <WSActions
        orientation="vertical"
        buttons={[
          {
            label: "Activate tax withholdings",
            onClick: activate,
            loading: activateMeta.isLoading
          },
          {
            kind: "Link",
            label: "Skip, I’ll handle taxes myself",
            onClick: () => {
              setContext(prev => ({
                ...prev,
                taxWithholdingsSkipped: true
              }));
              props.onNext?.("skip");
            }
          }
        ]}
      />
    </WSList>
  );
};
